import {
  Box,
  FormControlLabel,
  OutlinedInput,
  OutlinedInputProps,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useThemeMode } from "../../theme/atom";

interface LabeledInputProps extends OutlinedInputProps {
  label: string;
  placeholder?: string;
  /**
   * Fixed width for the label column. Omit to split the row evenly, which is
   * what a field standing on its own wants; pass a shared value to line several
   * fields' inputs up with each other regardless of label length.
   */
  labelWidth?: number | string;
  /**
   * `"top"` puts a small caption above the input instead of a label beside it,
   * so the input gets the field's full width whatever the label's length —
   * what fields laid side by side in a row want. The placeholder still repeats
   * the label either way, as a hint inside the empty input.
   */
  labelPlacement?: "start" | "top";
}

const LabeledInput = (props: LabeledInputProps) => {
  const {
    label,
    labelWidth,
    labelPlacement,
    value,
    onChange,
    ...propsWithoutLabel
  } = props;
  const [inputValue, setInputValue] = useState(value ?? "");
  const [themeMode] = useThemeMode();

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  const className = props.label.toLowerCase().replaceAll(" ", "-");
  const top = labelPlacement === "top";
  const labelFlex =
    labelWidth === undefined
      ? 1
      : `0 0 ${typeof labelWidth === "number" ? `${labelWidth}px` : labelWidth}`;

  return (
    <Stack direction={top ? "column" : "row"} spacing={top ? 0.25 : 1}>
      {top ? (
        <Typography
          component="label"
          htmlFor={`${className}-input`}
          noWrap
          sx={{
            // Sized like the GRID / PRODUCT captions in the card above, but bold:
            // with the label repeated as a placeholder, the caption needs the
            // weight to read as the heading rather than more hint text.
            fontSize: 10,
            fontWeight: "bold",
            lineHeight: 1.2,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: props.error ? "error.main" : "text.secondary",
          }}
        >
          {label}
        </Typography>
      ) : (
        <FormControlLabel
          sx={{
            margin: 0,
            flex: labelFlex,
            // Sits the label against its input rather than adrift at the far left
            // of the column.
            "& span": {
              flex: 1,
              textAlign: "left",
            },
          }}
          label={label}
          control={<Box />}
        />
      )}
      <OutlinedInput
        size="small"
        {...propsWithoutLabel}
        sx={{
          flex: 1,

          // Lets the input take the rest of the row rather than being held at
          // its content width when a label column is pinned.
          minWidth: 0,

          // bgcolor: "background.paper"
          "& .MuiOutlinedInput-input": {
            "&.Mui-disabled": {
              backgroundColor: (t) => t.palette.background.default,
              opacity: 0.5,
              borderRadius: themeMode === "tokyoNight" ? "5px" : "12px",
            },
          },
        }}
        id={`${className}-input`}
        placeholder={props.placeholder ?? props.label}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (onChange) {
            onChange(e);
          }
        }}
      />
    </Stack>
  );
};

export { LabeledInput };
